import React from 'react';
import { Button } from '@cimpress/react-components';
import { Truck } from '../../models/truck';
import Barcode from '../../common/components/Barcode';
import { openPrintDialogForComponent } from '../../common/openPrintDialogForComponent';
import { Location } from '../../locations/locationsModels';
import { Document } from '../../models/document';
import Link from '../../common/Link';
import { getStatusTileProperties, getTruckManifestStatus, TruckManifestStatus } from '../trucksUtils';
import TileComponent from '../../common/components/TileComponent';
import moment from 'moment';
import { TruckManifestComponentV2 } from './TruckManifestComponent.v2';
import { TruckManifestComponent } from './TruckManifestComponent';
import LoadTruckFormContainer from './LoadTruckFormContainer';
import { ModalAction } from '../../contexts/modal-context';
import EditTruckFormContainer from './EditTruckFormContainer';
import EditTruckFormContainerV2 from './EditTruckFormContainer.v2';

import ShowTruckFormContainer from './ShowTruckFormContainer';
import ManifestTruckFormContainer from './ManifestTruckFormContainer';
import { FeatureToggle } from '../../components/utils/FeatureToggle';
import { Features } from '../../models/features';
import { FlexDirection, FlexBox, AlignItems, FlexShrink, FlexGrow } from '../../utils/css-classes';
import LoadShipmentGroupForm from '../../components/forms/LoadShipmentGroupForm';
import ShowCannotDeleteTruckComponent from './ShowCannotDeleteTruckComponent';
import DeleteTruckContainer from './DeleteTruckContainer';

export interface Props {
  truck: Truck;
  lineHaulCompanyName?: string;
  saveDocument: (document: Document) => void;
  selectedLocation: Location;
  highlight?: string;
  matchType?: number;
  modal: ModalAction;
}

function isManifestDisabled(truck: Truck) {
  return ['venlo', 'venlo-demo'].includes(truck.locationKey) &&
    typeof truck.name === 'string' &&
    truck.name.toLowerCase().trim().startsWith('staging');
}

// tslint:disable-next-line:variable-name
const LoadTruckForm = ({ hide, truck }: { hide: () => void; truck: Truck; }) => <>
  <FeatureToggle whenFlag={Features.CUSTOM_SHIPMENT_GROUPS} isEnabled>
    <LoadShipmentGroupForm truck={truck} hide={hide}/>
  </FeatureToggle>
  <FeatureToggle whenFlag={Features.CUSTOM_SHIPMENT_GROUPS} isDisabled>
    <LoadTruckFormContainer truck={truck} hide={hide}/>
  </FeatureToggle>
</>;

// tslint:disable-next-line:function-name
export default function TruckComponent({
  truck,
  lineHaulCompanyName,
  saveDocument,
  selectedLocation,
  highlight,
  matchType,
  modal,
}: Props) {

  const printBarcode = () => openPrintDialogForComponent(
    <Barcode
      selectedLocation={selectedLocation}
      title={truck.name}
      barcode={truck.barcode}
      lineHaulCompanyName={lineHaulCompanyName}
    />,
  );

  const getStatusTile = function getStatusTile(truck: Truck) {
    const truckManifestStatus = getTruckManifestStatus(truck);
    const closedTime =
      truckManifestStatus === TruckManifestStatus.Error
        || truckManifestStatus === TruckManifestStatus.Success
        ? moment(truck.closedAt).tz(selectedLocation.localeSettings.timezone).format('HH:mm')
        : '';
    const { iconClassName, title } = getStatusTileProperties(truckManifestStatus);

    let emailAddress = 'pagelogisticssupport@cimpress.com,mPBM@vistaprint.com';
    switch (selectedLocation.key) {
      case 'windsor':
        emailAddress += ',WNDITOps@vistaprint.com';
        break;
      case 'venlo':
        emailAddress += ',VenloITOps@vistaprint.com';
        break;
    }
    const emailSubject = encodeURIComponent('URGENT REQUEST FOR SUPPORT - MANIFEST FAILED');
    const emailBody = encodeURIComponent(`Hello Support Team,

We experienced an error while manifesting the truck with truck ID:
${truck.id}

This is a production incident and we are paging you to request assistance.`);

    const mailTo = `mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`;
    const shouldDisplayContactSupport = truckManifestStatus === TruckManifestStatus.Error;

    return (
      <div className={`${FlexShrink.SHRINK_0} text-center`}>
        <TileComponent
          title={`${title} ${closedTime}`}
          iconClassName={iconClassName}
          onClick={showLoadForm(truckManifestStatus)}
        />
        <FeatureToggle whenFlag={Features.CONTACT_SUPPORT_LINK} isEnabled={true}>
          {shouldDisplayContactSupport && <a href={mailTo}>Page Support</a>}
        </FeatureToggle>
      </div>
    );
  };

  const copyBarcode = (event: React.MouseEvent) => {
    event.preventDefault();
    const body = document.querySelector('body');
    const input = document.createElement('input');
    input.value = truck.barcode;
    body!.appendChild(input);
    input.select();
    document.execCommand('copy');
    body!.removeChild(input);
  };

  const printBarcodeLink = (
    <span className="m-4">
      <span className="mr-2 fa fa-barcode text-primary" />
      <Link
        text="Print Barcode"
        onClick={printBarcode}
      />
      <span className="m-4">
        <a href="#" onClick={copyBarcode} title="Copy barcode">
          <span className="fas fa-copy" />
        </a>
      </span>
    </span>
  );

  const state: TruckManifestStatus = getTruckManifestStatus(truck);

  const showManifestForm = () =>
    modal(
      [<h4 key="h4">{'Manifest Truck'}</h4>, <h5 key="h5">{truck.name}</h5>],
      hide => <ManifestTruckFormContainer truck={truck} hide={hide} />,
      { bsStyle: 'info' },
    );

  const showEditForm = () =>
    modal(
      [<h4 key="h4">{'Edit Truck'}</h4>, <h5 key="h5">{truck.name}</h5>],
      hide => <>
        <FeatureToggle whenFlag={Features.CUSTOMS_BROKER_AOT} isEnabled>
          <EditTruckFormContainerV2 truck={truck} hide={hide} />
        </FeatureToggle>
        <FeatureToggle whenFlag={Features.CUSTOMS_BROKER_AOT} isDisabled>
          <EditTruckFormContainer truck={truck} hide={hide} />
        </FeatureToggle>
      </>,
      { bsStyle: 'info' },
    );

  const showTruckDetails = () =>
    modal(
      [<h4 key="h4">{'Truck Details'}</h4>, <h5 key="h5">{truck.name}</h5>],
      hide => <ShowTruckFormContainer truck={truck} hide={hide} />,
      { bsStyle: 'info' },
    );

  const showDeleteWarning = () => {
    if (truck.palletBoxes.length === 0) {
      modal(
        [<h4 key="h4">{'Delete Truck'}</h4>, <h5 key="h5">{truck.name}</h5>],
        hide => <DeleteTruckContainer truck={truck} hide={hide} />,
        { bsStyle: 'info' },
      );
    } else {
      modal(
        [<h4 key="h4">{'Cannot Delete Truck'}</h4>, <h5 key="h5">{truck.name}</h5>],
        hide => <ShowCannotDeleteTruckComponent hide={hide}/>,
        { bsStyle: 'info' },
      );
    }
  };

  const showLoadForm = function showLoadForm(truckManifestStatus: TruckManifestStatus) {
    return truckManifestStatus === TruckManifestStatus.Ready
      ? () =>
        modal(
          [<h4 key="h4">{'Load Shipment Group'}</h4>, <h5 key="h5">{truck.name}</h5>],
          hide => <LoadTruckForm truck={truck} hide={hide} />,
          { bsStyle: 'info' },
        )
      : undefined;
  };

  const statusPart = getStatusTile(truck);

  let actions = null;
  switch (state) {
    case TruckManifestStatus.Ready:
      actions = (
        <div className={`${FlexBox.FLEX} ${FlexDirection.COLUMN}`}>
          <Button
            className="min-width-180"
            type="primary"
            disabled={Boolean(truck.manifest || isManifestDisabled(truck))}
            onClick={showManifestForm}
          >
            Generate Manifest
          </Button>
          <span className="mx-4 mt-4">
            <span className="mr-2 fa fa-edit text-primary" />
            <Link
              className=""
              text="Edit Truck Details"
              onClick={(showEditForm)}
            />
          </span>
          <span className="mx-4 mt-4">
            <span className="mr-2 fa fa-trash text-primary"/>
            <Link
              className=""
              text="Delete Truck"
              onClick={(showDeleteWarning)}
            />
          </span>
          {printBarcodeLink}
        </div>
      );
      break;
    case TruckManifestStatus.InProgress:
      actions = (
        <div className={`${FlexBox.FLEX} ${FlexDirection.COLUMN}`}>
          {/* <Button className="min-width-180" type="primary" disabled={true}>
            Manifest Details
          </Button> */}
          <span className="mx-4 mt-4">
            <span className="mr-2 fa fa-search text-primary" />
            <Link
              className=""
              text="Show Truck Details"
              onClick={showTruckDetails}
            />
          </span>
          {printBarcodeLink}
        </div>
      );
      break;
    case TruckManifestStatus.Error:
    case TruckManifestStatus.Success:
    default:
      actions = (
        <div className={`${FlexBox.FLEX} ${FlexDirection.COLUMN}`}>
          <FeatureToggle whenFlag={Features.SHOW_MANIFEST_LOG} isEnabled={true}>
            <a className="min-width-180 btn btn-primary" href={`/manifests/${truck.manifest!.id}/event-log`}>
              Manifest Log
            </a>
          </FeatureToggle>
          <span className="mx-4 mt-4">
            <span className="mr-2 fa fa-search text-primary" />
            <Link
              className=""
              text="Show Truck Details"
              onClick={showTruckDetails}
            />
          </span>
          {printBarcodeLink}
        </div>
      );
      break;
  }

  const actionPart = (
    <div className={`${FlexBox.FLEX} ${FlexDirection.COLUMN} ${AlignItems.CENTER} ${FlexShrink.SHRINK_0}`}>
      {actions}
    </div>
  );

  const totalShipmentCount = truck.palletBoxes.reduce((acc, cur) => acc + cur.shipmentCount, 0);

  return (
    <div key={truck.id} className="my-4 card card-minimal">
      <a id={`truck-${truck.id}`} />
      <div className={`card-header card-title ${FlexBox.FLEX} ${AlignItems.CENTER}`}>
        <span className="mr-3 fas fa-2x fa-truck" />
        <span className={`h5 ${FlexGrow.GROW_1}`}>{truck.name}</span>
        <span className="h5" title="Shipments" >
          {totalShipmentCount}&nbsp;
          <span className="mr-3 fas fa-boxes" />
        </span>
      </div>
      <div className="m-2 card-body p-3">
        <div className={`${FlexBox.FLEX} ${AlignItems.STRETCH}`}>
          {statusPart}
          <span className="mx-4 divider-v" />
          <FeatureToggle whenFlag={Features.PB_COUNT_WINDSOR} isEnabled={false}>
            <TruckManifestComponent
              truck={truck}
              saveDocument={saveDocument}
              highlight={highlight}
              matchType={matchType}
            />
          </FeatureToggle>
          <FeatureToggle whenFlag={Features.PB_COUNT_WINDSOR} isEnabled={true}>
            <TruckManifestComponentV2
              truck={truck}
              saveDocument={saveDocument}
              highlight={highlight}
              matchType={matchType}
            />
          </FeatureToggle>
          <span className="mx-4 divider-v" />
          {actionPart}
        </div>
      </div>
    </div>
  );
}
